import { breakpoints, Card, Dialog, Inline, vars } from '@etg/wings';
import { useMemo, useState } from 'react';
import type { BoundNew } from '@eti/schema-types';
import { css } from '@eti/styles';
import { useDirection, useSiteContext, useTranslation } from '@eti/providers';
import { useMediaQuery } from 'usehooks-ts';
import type {
  AvailableExtraProductsType,
  BoundsType,
  IncludedCabinBaggageType,
  IncludedCheckedBaggageType,
} from '../../../../result/models';
import BaggageInformationTitle from '../../../../result/components/v3/drawer/baggage/BaggageInformationTitle';
import { TripBaggageInfo } from '../../../../result/components/v3/resultTripCards/components/TripBaggageInfo';
import type {
  RebookingTripBoundsType,
  IncludedCabinBaggageType as RebookingTripIncludedCabinBaggageType,
} from '../../../../change-trip/models';
import { hasLessThan2hSelfTransfer } from '../../../../result/components/v3/utils/trip';
import { BaggageInformationBody } from '../../../../result/components/v3/drawer/baggage/BaggageInformationBody';
import { BaggageList } from '../../../../result/components/v3/resultTripCards/components/BaggageList';

export type BaggageInfo = {
  availableExtraProducts: AvailableExtraProductsType;
  includedCabinBaggage: IncludedCabinBaggageType | RebookingTripIncludedCabinBaggageType;
  includedCheckedBaggage?: IncludedCheckedBaggageType | null;
};

interface SelectedTripBaggageInfoProps {
  availableExtraProducts: AvailableExtraProductsType;
  bounds: BoundNew[];
  includedCabinBaggage: IncludedCabinBaggageType;
  includedCheckedBaggage: IncludedCheckedBaggageType;
}
const wrapperStyles = css`
  display: inline;
`;
const baggageStyle = css`
  border-top: 1px solid ${vars.colors.divider};
  justify-content: space-between;
  padding-top: 16px;
`;

const TripBaggageClassStyles = (isRTL: boolean) => css`
  left: ${isRTL ? '-10px' : 'inherit'};
  right: ${isRTL ? 'inherit' : '-10px'};
  top: 45px;
`;

const cardSectionStyles = css`
  align-items: center;
  border-bottom: 1px solid ${vars.colors.divider};
  display: flex;
  justify-content: space-between;
`;

const baggageDialogStyles = css`
  background-color: ${vars.colors.page.background.primary};
`;

type NewBoundsType = BoundsType | RebookingTripBoundsType;

const SelectedTripBaggageInfo = ({
  availableExtraProducts,
  bounds,
  includedCabinBaggage,
  includedCheckedBaggage,
}: SelectedTripBaggageInfoProps) => {
  const { isRTL } = useDirection();
  const { t } = useTranslation();
  const { language, timeFormat } = useSiteContext();
  const isMobileDevice = useMediaQuery(`(max-width: ${breakpoints._768})`);
  const isTabletDevice = useMediaQuery(`(max-width: ${breakpoints._970})`);
  const [showProductDetails, setShowProductDetails] = useState(false);

  const baggageInfo = {
    availableExtraProducts,
    includedCabinBaggage,
    includedCheckedBaggage,
  } as BaggageInfo;

  const hasLessThan2hSelfTransferTrip = useMemo(() => {
    return hasLessThan2hSelfTransfer(language, timeFormat, bounds as unknown as BoundsType);
  }, [language, timeFormat, bounds]);

  const handleClick = () => {
    if (isMobileDevice) {
      setShowProductDetails(true);
    }
  };

  return (
    <>
      <Inline
        align="center"
        className={baggageStyle}
        data-testid="your-selected-trip-page-selected-trip-baggage-info"
        onClick={handleClick}
      >
        <BaggageInformationTitle />
        <div className={wrapperStyles}>
          <TripBaggageInfo
            availableExtraProducts={availableExtraProducts}
            bounds={bounds as unknown as NewBoundsType}
            className={isTabletDevice ? TripBaggageClassStyles(isRTL) : ''}
            includedCabinBaggage={includedCabinBaggage}
            includedCheckedBaggage={includedCheckedBaggage}
          />
        </div>
      </Inline>
      <Dialog
        className={baggageDialogStyles}
        isOpen={showProductDetails}
        onDismiss={() => setShowProductDetails(false)}
        title={t('YourSelectedTrip.Baggage.Information.Title.Text')}
      >
        <Card>
          <Card.Section className={cardSectionStyles} data-testid="baggage-information-title">
            <BaggageInformationTitle isResultCardBaggageEnabled={false} />
            <BaggageList baggageInfo={baggageInfo} spacing={8} />
          </Card.Section>
          <BaggageInformationBody
            baggageInfo={baggageInfo}
            hasLessThan2hSelfTransfer={hasLessThan2hSelfTransferTrip}
            isInDrawer
            isResultCardBaggageEnabled={false}
          />
        </Card>
      </Dialog>
    </>
  );
};
export default SelectedTripBaggageInfo;
